import { queryOptions, useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import type { TicketRating } from '@erp/shared';
import { api } from '@/lib/api-client';
import { cn, formatDateTime } from '@/lib/utils';

function ticketRatingQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'rating', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TicketRating | null>(`/tickets/${ticketNumber}/rating`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * Card pequeno da avaliação CSAT (GET /tickets/:n/rating): estrelas + score,
 * comentário e data quando respondida; "Pesquisa enviada, sem resposta"
 * quando só enviada; não renderiza nada quando null.
 */
export function TicketRatingCard({ ticketNumber }: { ticketNumber: number }) {
  const ratingQ = useQuery(ticketRatingQuery(ticketNumber));

  // Sem pesquisa (null), carregando ou erro: o card simplesmente não aparece.
  if (!ratingQ.data) return null;
  const rating = ratingQ.data;

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <h2 className="mb-3 text-base font-semibold">Avaliação de atendimento</h2>
      {rating.score === null ? (
        <p className="text-sm text-muted-foreground">
          Pesquisa enviada, sem resposta
          {rating.sentAt ? ` (enviada em ${formatDateTime(rating.sentAt)})` : ''}.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span
              className="flex items-center gap-0.5"
              role="img"
              aria-label={`Nota ${rating.score} de 5`}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={cn(
                    'size-4.5',
                    i < (rating.score ?? 0)
                      ? 'fill-[#FADB14] text-[#FADB14]'
                      : 'text-muted-foreground/40'
                  )}
                />
              ))}
            </span>
            <span className="text-sm font-semibold tabular-nums">{rating.score}/5</span>
          </div>
          {rating.comment ? (
            <blockquote className="rounded-md bg-muted px-3 py-2 text-sm break-words whitespace-pre-wrap">
              {rating.comment}
            </blockquote>
          ) : null}
          {rating.respondedAt ? (
            <p className="text-xs text-muted-foreground">
              Respondida em {formatDateTime(rating.respondedAt)}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}
